import styled from "@emotion/styled"
import { Value } from "@stringtale/react"
import getContentSubjectTitle from "app/content-subjects/getContentSubjectTitle"
import { useCurrentOrganization } from "app/core/hooks/useCurrentOrganization"
import { useIsPublic } from "app/core/hooks/useIsPublic"
import { ContentSubjectType, LessonType } from "db/enums"
import { ComponentProps, useMemo } from "react"
import { DIMMED_BLUE_COLOR } from "theme/colors"
import Text from "ui/Text"
import getHasSubmittable, {
  GetHasSubmittableLesson,
} from "../utils/getHasSubmittable"
import getLessonTypeTitle from "../utils/getLessonTypeTitle"

export type LearningLine = {
  period: string | null
  cardLabel: string | null
}

export type LessonSubTitleLesson = GetHasSubmittableLesson & {
  id?: number
  contentSubject: ContentSubjectType
  lessonType: LessonType
  typeTitle: string | null
  featuredThemeTitle: string | null
  isLearningLineLesson: boolean
  learningLines?: {
    LearningLineLessonGroup: {
      learningLineContentPagePlanningLessonGroups: {
        cardLabel: string | null
      } | null
    }
  }[]
}

const Root = styled(Text)`
  display: flex;
  gap: 5px;
  color: ${DIMMED_BLUE_COLOR};
`

type Props = {
  lesson: LessonSubTitleLesson
  size?: string
  hasLearningLines: boolean
} & ComponentProps<typeof Root>

export default function LessonSubTitle({
  lesson,
  hasLearningLines,
  ...props
}: Props) {
  const hasSpecialLearningLineTreatment =
    useCurrentOrganization()?.subscription.hasSpecialLearningLineTreatment ||
    false
  const hasSubmittable = useMemo(() => getHasSubmittable(lesson), [lesson])
  const isPublic = useIsPublic()

  const learningLineCardLabel = useMemo(() => {
    return lesson.learningLines?.[0]?.LearningLineLessonGroup
      ?.learningLineContentPagePlanningLessonGroups?.cardLabel
  }, [lesson])

  return (
    <Root {...props}>
      {!hasSpecialLearningLineTreatment &&
      hasLearningLines &&
      lesson.isLearningLineLesson ? (
        <>
          <Value name="apps.web.src.lessons.components.lessonsubtitle.leerlijn">
            Leerlijn
          </Value>{" "}
          {getContentSubjectTitle(lesson.contentSubject)}{" "}
          {learningLineCardLabel ? <>{learningLineCardLabel} </> : null}
          {"| "}
        </>
      ) : (
        <>
          {getContentSubjectTitle(lesson.contentSubject)} {"| "}
        </>
      )}
      {lesson.typeTitle || getLessonTypeTitle(lesson.lessonType)}{" "}
      {lesson.featuredThemeTitle && (
        <>
          {"| "}
          {lesson.featuredThemeTitle}{" "}
        </>
      )}
      {hasSubmittable && !isPublic ? (
        <>
          {"| "}
          <Value name="apps.web.src.lessons.components.lessonsubtitle.inleveropdracht">
            Inleveropdracht
          </Value>
        </>
      ) : null}
    </Root>
  )
}
